/**
 * Contabilidad › Configuración › Períodos (sprint S1, ítem S1-05).
 *
 * Dos calendarios, como en la propuesta aprobada el 24/10/2026:
 *   · el fiscal legal, que fija el país (Costa Rica: enero a diciembre) y no se edita;
 *   · el de gestión, que elige la empresa: mensual o 13 períodos de 4 semanas.
 *
 * Cambiar el de gestión mueve asientos de período, así que primero se muestra
 * qué pasaría (vista previa) y solo después se aplica. La opción de 13 períodos
 * aparece cuando el servidor la habilita (`opciones.trecePeriodos`).
 */
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Calendar, CheckCircle2, Lock, AlertTriangle, Eye, RefreshCw } from 'lucide-react';
import { contabilidadApi } from '../../utils/api';

const CONVENCIONES = {
  iso: { diaInicioSemana: 1, semana1: 'contiene_4_enero', etiqueta: 'ISO: lunes a domingo, semana 1 la del 4 de enero' },
  corbana: { diaInicioSemana: 7, semana1: 'contiene_1_enero', etiqueta: 'CORBANA: domingo a sábado, semana 1 la del 1 de enero' }
};
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'setiembre', 'octubre', 'noviembre', 'diciembre'];

const fecha = (v) => {
  const d = String(v || '').slice(0, 10);
  return d ? new Date(`${d}T00:00:00`).toLocaleDateString('es-CR') : '';
};
const convencionDe = (c) => (c?.diaInicioSemana === 7 && c?.semana1 === 'contiene_1_enero' ? 'corbana' : 'iso');
const anioDe = (p) => p.anioGestion || Number(String(p.fechaInicio).slice(0, 4));

export default function CalendarioPeriodos({ periodos, onRecargar, onCerrarPeriodo, onReabrirPeriodo }) {
  const [calendario, setCalendario] = useState(null);
  const [form, setForm] = useState({ tipo: 'mensual', convencion: 'iso', anioDesde: '', mesInicioFiscal: 1 });
  const [vista, setVista] = useState(null);
  const [trabajando, setTrabajando] = useState(false);
  const [anio, setAnio] = useState(new Date().getFullYear());

  const cargar = async () => {
    try {
      const r = await contabilidadApi.getCalendario();
      const c = r.data.data;
      setCalendario(c);
      setForm({
        tipo: c.tipo,
        convencion: convencionDe(c),
        anioDesde: c.anioDesde || new Date().getFullYear() + 1,
        mesInicioFiscal: c.mesInicioFiscal || 1
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al cargar el calendario');
    }
  };

  useEffect(() => { cargar(); }, []);

  const pedido = () => {
    const mesInicioFiscal = Number(form.mesInicioFiscal) || 1;
    if (form.tipo !== 'cuatro_semanas') return { tipo: 'mensual', diaInicioSemana: 1, semana1: 'contiene_4_enero', anioDesde: null, mesInicioFiscal };
    const { diaInicioSemana, semana1 } = CONVENCIONES[form.convencion];
    return { tipo: 'cuatro_semanas', diaInicioSemana, semana1, anioDesde: Number(form.anioDesde), mesInicioFiscal };
  };

  const revisar = async () => {
    setTrabajando(true);
    try {
      const r = await contabilidadApi.previsualizarCalendario(pedido());
      setVista(r.data.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al revisar el cambio');
    } finally {
      setTrabajando(false);
    }
  };

  const aplicar = async () => {
    setTrabajando(true);
    try {
      const r = await contabilidadApi.guardarCalendario(pedido());
      const d = r.data.data;
      toast.success(`Calendario actualizado: ${d.movidos} asiento(s) reubicados`);
      setVista(null);
      await cargar();
      onRecargar();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al cambiar el calendario');
    } finally {
      setTrabajando(false);
    }
  };

  const generar = async () => {
    setTrabajando(true);
    try {
      const r = await contabilidadApi.generarAnioPeriodos(anio);
      toast.success(`${r.data.data.length} períodos de ${anio} listos`);
      onRecargar();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al generar el año');
    } finally {
      setTrabajando(false);
    }
  };

  const delAnio = useMemo(
    () => periodos
      .filter(p => ['mensual', 'cuatro_semanas'].includes(p.tipo) && anioDe(p) === Number(anio))
      .sort((a, b) => String(a.fechaInicio).localeCompare(String(b.fechaInicio))),
    [periodos, anio]
  );
  const otros = useMemo(
    () => periodos.filter(p => !['mensual', 'cuatro_semanas'].includes(p.tipo)),
    [periodos]
  );

  const sinCambios = calendario && form.tipo === calendario.tipo && Number(form.mesInicioFiscal) === (calendario.mesInicioFiscal || 1) &&
    (form.tipo === 'mensual' || (convencionDe(calendario) === form.convencion && Number(form.anioDesde) === calendario.anioDesde));
  const fiscal = calendario?.calendarioFiscal;

  return (
    <div className="space-y-6" data-prueba="calendario-periodos">
      <div>
        <h3 className="font-semibold text-gray-900">Calendario contable</h3>
        <p className="text-sm text-gray-500">
          El calendario fiscal lo fija la ley; el de gestión define los períodos en que se cierran los asientos.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="border rounded-lg p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Calendario fiscal legal</p>
          <p className="mt-1 font-medium text-gray-900" data-prueba="calendario-fiscal">
            {fiscal ? `Del 1 de ${MESES[(Number(form.mesInicioFiscal) || 1) - 1]} al ${Number(form.mesInicioFiscal) === 1 ? '31 de diciembre' : `último día de ${MESES[(Number(form.mesInicioFiscal) + 10) % 12]}`}` : '…'}
          </p>
          {fiscal?.baseLegal && <p className="mt-1 text-xs text-gray-500">{fiscal.baseLegal}</p>}
          {/* REVISIÓN 25/10: el año fiscal es por empresa. Por ley es de enero a diciembre; se cambia
              solo si Hacienda le autorizó un período especial (siempre de meses completos). */}
          <label className="mt-3 block text-sm text-gray-700">
            Mes de inicio del año fiscal
            <select name="mesInicioFiscal" value={form.mesInicioFiscal}
              onChange={(e) => { setForm({ ...form, mesInicioFiscal: Number(e.target.value) }); setVista(null); }}
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm">
              {MESES.map((m, i) => <option key={m} value={i + 1}>{m.charAt(0).toUpperCase() + m.slice(1)}{i === 0 ? ' (por ley)' : ''}</option>)}
            </select>
          </label>
          <p className="mt-1 text-xs text-gray-500">Cámbielo solo si Hacienda le autorizó un período fiscal especial. Lo usan las declaraciones, el cierre del año y los 13 períodos.</p>
        </div>

        <div className="border rounded-lg p-4 space-y-3">
          <p className="text-xs uppercase tracking-wide text-gray-500">Calendario de gestión</p>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="tipoCalendario" value="mensual" checked={form.tipo === 'mensual'}
                onChange={() => { setForm({ ...form, tipo: 'mensual' }); setVista(null); }} />
              Mensual (12 períodos, uno por mes)
            </label>
            {calendario?.opciones?.trecePeriodos && (
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="tipoCalendario" value="cuatro_semanas" checked={form.tipo === 'cuatro_semanas'}
                  onChange={() => { setForm({ ...form, tipo: 'cuatro_semanas' }); setVista(null); }} />
                13 períodos de 4 semanas
              </label>
            )}
          </div>

          {form.tipo === 'cuatro_semanas' && (
            <div className="space-y-2 pl-6">
              <label className="block text-sm text-gray-700">
                Semanas
                <select value={form.convencion} name="convencion"
                  onChange={(e) => { setForm({ ...form, convencion: e.target.value }); setVista(null); }}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm">
                  {Object.entries(CONVENCIONES).map(([k, v]) => <option key={k} value={k}>{v.etiqueta}</option>)}
                </select>
              </label>
              <label className="block text-sm text-gray-700">
                Rige desde el año
                <input type="number" name="anioDesde" value={form.anioDesde} min="2000" max="2999"
                  onChange={(e) => { setForm({ ...form, anioDesde: e.target.value }); setVista(null); }}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
              </label>
              <p className="text-xs text-gray-500">
                El período 1 empieza el primer día del año fiscal y el 13 termina el último, así que esos dos pueden no ser de 4 semanas exactas. Los años anteriores siguen mensuales.
              </p>
            </div>
          )}

          <button onClick={revisar} disabled={trabajando || sinCambios}
            className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-40 text-sm">
            <Eye className="h-4 w-4" />
            Revisar el cambio
          </button>
        </div>
      </div>

      {vista && (
        <div className="border rounded-lg p-4 space-y-3" data-prueba="vista-previa">
          <p className="font-medium text-gray-900">Qué pasaría</p>
          {!vista.desde ? (
            <p className="text-sm text-gray-600">No cambia el período de ningún asiento.</p>
          ) : (
            <p className="text-sm text-gray-600">
              Desde el {fecha(vista.desde)}: {vista.asientos} asiento(s) en el rango, <strong>{vista.asientosQueSeMueven} cambian de período</strong>.
              {/* REVISIÓN 25/10: el número de asiento es por período; al mudarse recibe uno nuevo. Se avisa antes de aplicar. */}
              {vista.asientosQueSeMueven > 0 && <> Esos asientos reciben un <strong>número nuevo</strong> dentro de su período nuevo.</>}
            </p>
          )}
          {vista.periodosDestino?.length > 0 && (
            <div className="max-h-64 overflow-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr><th className="text-left p-2">Período</th><th className="text-left p-2">Fechas</th><th className="text-right p-2">Asientos</th><th className="text-right p-2">Entran</th></tr>
                </thead>
                <tbody>
                  {vista.periodosDestino.map(p => (
                    <tr key={p.desde} className="border-t">
                      <td className="p-2">{p.nombre}</td>
                      <td className="p-2">{fecha(p.desde)} a {fecha(p.hasta)}</td>
                      <td className="p-2 text-right">{p.asientos}</td>
                      <td className="p-2 text-right">{p.entran}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {vista.bloqueado && (
            <div className="flex gap-2 p-3 border border-red-300 rounded text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                No se puede aplicar: hay períodos cerrados desde el {fecha(vista.desde)} ({vista.periodosCerrados.map(p => p.nombre).join(', ')}).
                Reábralos o elija un año posterior.
              </span>
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button onClick={() => setVista(null)} className="px-4 py-2 text-gray-600 hover:text-gray-900 text-sm">Cancelar</button>
            <button onClick={aplicar} disabled={trabajando || vista.bloqueado}
              className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-40 text-sm">
              Aplicar el cambio
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">Períodos</h3>
          <p className="text-sm text-gray-500">Los del año elegido, según el calendario de gestión.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="number" value={anio} onChange={(e) => setAnio(e.target.value)} name="anioPeriodos"
            className="w-24 px-3 py-2 border rounded-lg text-sm" aria-label="Año" />
          <button onClick={generar} disabled={trabajando}
            className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-40 text-sm">
            <RefreshCw className="h-4 w-4" />
            Generar el año
          </button>
        </div>
      </div>

      {delAnio.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p>No hay períodos de {anio}. Use «Generar el año».</p>
        </div>
      ) : (
        <div className="divide-y border rounded-lg" data-prueba="lista-periodos">
          {delAnio.map((periodo) => (
            <div key={periodo.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
              <div>
                <p className="font-medium text-gray-900">{periodo.nombre}</p>
                <p className="text-sm text-gray-500">
                  {fecha(periodo.fechaInicio)} - {fecha(periodo.fechaFin)} · {periodo.totalAsientos || 0} asiento(s)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                  periodo.estado === 'abierto' ? 'bg-green-100 text-green-700'
                    : periodo.estado === 'cerrado' ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-700'
                }`}>
                  {periodo.estado === 'abierto' && <CheckCircle2 className="h-3 w-3" />}
                  {periodo.estado === 'cerrado' && <Lock className="h-3 w-3" />}
                  {periodo.estado}
                </span>
                {periodo.estado === 'abierto' && (
                  <button onClick={() => onCerrarPeriodo(periodo.id)} className="text-sm text-red-600 hover:text-red-700">
                    Cerrar
                  </button>
                )}
                {/* Reabrir: la puerta existía en la API y la pantalla no la ofrecía (S2-01). */}
                {periodo.estado === 'cerrado' && onReabrirPeriodo && (
                  <button onClick={() => onReabrirPeriodo(periodo.id)} className="text-sm text-indigo-600 hover:text-indigo-700">
                    Reabrir
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {otros.length > 0 && (
        <p className="text-xs text-gray-500">
          También hay {otros.length} período(s) de otro tipo ({otros.map(p => p.nombre).join(', ')}), creados antes del calendario de gestión.
        </p>
      )}
    </div>
  );
}
